import { GaugeIcon } from '@phosphor-icons/react'

import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { getAccountIcon } from '@pages/ConsolidatedEntry/components/ConsolidatedGrid/helpers/getAccountIcon'

import type { ColumnDefProps } from '@components/shared/Table/types'
import type { BranchClassificationDetailAccountProps } from '@services/api/previewManagement/types'

import * as S from '../styles'

const isGrossMarginRow = (account: string) => account.toLowerCase().includes('margin')
const isGrossProfitRow = (account: string) => account.toLowerCase().includes('profit')

const formatPercentage = (value: number) => `${value.toFixed(2)}%`.replace('.', ',')

const formatValue = (value: number, isPercentageRow: boolean) => (isPercentageRow ? formatPercentage(value) : currencyMask(value))

const renderDeviationCell = (row: BranchClassificationDetailAccountProps, isPercentageRow: boolean) => {
  const isNegative = row.deviationVsBudget < 0
  const isPositive = row.deviationVsBudget > 0

  if (isPercentageRow) {
    return (
      <S.DeviationWrapper>
        <S.DeviationValue isNegative={isNegative} isPositive={isPositive}>
          {`${isPositive ? '+' : ''}${row.deviationVsBudget.toFixed(2)}`.replace('.', ',')} pp
        </S.DeviationValue>
      </S.DeviationWrapper>
    )
  }

  return (
    <S.DeviationWrapper>
      <S.DeviationValue isNegative={isNegative} isPositive={isPositive}>
        {isPositive ? `+${currencyMask(row.deviationVsBudget)}` : currencyMask(row.deviationVsBudget)}
      </S.DeviationValue>

      {row.deviationVsBudgetPercentage === 0 ? (
        <S.DeviationDash>—</S.DeviationDash>
      ) : (
        <S.DeviationPill>{formatPercentage(row.deviationVsBudgetPercentage)}</S.DeviationPill>
      )}
    </S.DeviationWrapper>
  )
}

const buildRow = (columns: ColumnDefProps[], row: BranchClassificationDetailAccountProps): ColumnDefProps[] => {
  const isPercentageRow = isGrossMarginRow(row.account)
  const isTotalRow = isGrossProfitRow(row.account) || isPercentageRow
  const account = !isTotalRow ? getAccountIcon(row.account) : undefined
  const highlightColor = isPercentageRow ? emotionTheme.colors.blue.head : undefined

  return [
    {
      ...columns[0],
      value: account ? (
        <S.RowLabel>
          <S.AccountIconWrapper color={account.color}>{account.icon}</S.AccountIconWrapper>
          {row.account}
        </S.RowLabel>
      ) : isPercentageRow ? (
        <S.GmLabel>
          <GaugeIcon size={18} />
          {row.account}
        </S.GmLabel>
      ) : (
        row.account
      ),
      fontWeight: isTotalRow ? 600 : undefined,
      columnColor: highlightColor,
      bodyColumnColor: highlightColor,
      withoutTooltip: true
    },
    {
      ...columns[1],
      value: <S.CellValue>{formatValue(row.budget, isPercentageRow)}</S.CellValue>,
      fontWeight: isTotalRow ? 600 : undefined,
      columnColor: highlightColor,
      bodyColumnColor: highlightColor,
      withoutTooltip: true
    },
    {
      ...columns[2],
      value: isPercentageRow ? (
        <S.CellValue>{formatValue(row.actualPreview, isPercentageRow)}</S.CellValue>
      ) : (
        <S.CellWrapper>
          <S.CellValue>{formatValue(row.actualPreview, isPercentageRow)}</S.CellValue>
          <S.CellTrend>{formatPercentage(row.actualPreviewPercentage)}</S.CellTrend>
        </S.CellWrapper>
      ),
      fontWeight: isTotalRow ? 600 : undefined,
      columnColor: highlightColor,
      bodyColumnColor: highlightColor,
      withoutTooltip: true
    },
    {
      ...columns[3],
      value: <S.CellValue>{formatValue(row.realizedAmount, isPercentageRow)}</S.CellValue>,
      fontWeight: isTotalRow ? 600 : undefined,
      columnColor: highlightColor,
      bodyColumnColor: highlightColor,
      withoutTooltip: true
    },
    {
      ...columns[4],
      value: renderDeviationCell(row, isPercentageRow),
      fontWeight: isTotalRow ? 600 : undefined,
      columnColor: highlightColor,
      bodyColumnColor: highlightColor,
      withoutTooltip: true
    }
  ]
}

export const parseUnitDreGrid = (rows: BranchClassificationDetailAccountProps[] = []) => {
  const columns: ColumnDefProps[] = [
    {
      id: 'account',
      columnName: 'Conta Contábil',
      value: '',
      headerAlign: 'left',
      bodyAlign: 'left',
      minWidth: '12rem'
    },
    { id: 'budget', columnName: 'Budget', value: '', align: 'right', minWidth: '9rem' },
    { id: 'actualPreview', columnName: 'Prévia', value: '', align: 'right', minWidth: '9rem' },
    { id: 'realizado', columnName: 'Realizado', value: '', align: 'right', minWidth: '9rem' },
    { id: 'deviation', columnName: 'Desvio (Prévia vs Budget)', value: '', align: 'right', minWidth: '11rem' }
  ]

  const data: ColumnDefProps[][] = rows.map((row) => buildRow(columns, row))

  return { data }
}
